import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Typography, Button } from '@material-ui/core';
import moment from 'moment';
import VoucherForm from './VoucherForm';
import DirectDebitPaymentForm from './DirectDebitPaymentForm';
import ContentHeader from './ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import { getCurrencySymbol } from '../helpers/ticket';
import globalStyles from '../styles';

const styles = () => ({
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    imagePreview: {
        minHeight: '56px',
        margin: '20px auto 0',
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        justifyContent: 'center',
        '& > img': {
            maxWidth: '100%'
        },
        '@media only screen and (max-device-width: 400px)': {
            margin: '0 auto 0'
        }
    },
    paymentHelp: {
        ...globalStyles.link,
        padding: '1em 0',
        display: 'block'
    }
});

class ConfirmDirectDebitPurchase extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null
        };
    }

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    calcMonthlyPriceAfterDiscount = (ticket, priceMonthly) => {
        const { voucher } = this.state;

        if (voucher) {
            const monthlyDiscount =
                (Number(voucher.value) * 100) /
                Number(ticket.num_monthly_payments);
            return (Number(priceMonthly) * 100 - monthlyDiscount) / 100;
        }

        return Number(priceMonthly);
    };

    getVoucherMessage = (
        ticket,
        total,
        roundedMonthlyPricePenceAfterDiscount,
        priceMonthly,
        priceDeposit
    ) => {
        const hasFullValueVoucher = roundedMonthlyPricePenceAfterDiscount < 100;
        const hasMonthlyPayments = parseFloat(priceMonthly) > 0;
        const { voucher } = this.state;

        if (!hasMonthlyPayments) {
            return '';
        }

        if (!voucher) {
            return '';
        }

        if (hasFullValueVoucher) {
            return (
                <>
                    <Typography variant="body2" style={{ color: 'red' }}>
                        Full value voucher codes cannot be used with this type
                        of ticket.
                    </Typography>
                    <Typography variant="body2" style={{ color: 'red' }}>
                        Please contact your transport operator for an
                        alternative ticket to use your voucher on.
                    </Typography>
                </>
            );
        }

        return (
            <>
                <Typography variant="body2">
                    Monthly payments reduced to{' '}
                    {getCurrencySymbol(ticket.currency)}
                    {(roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2)}.
                </Typography>
                <Typography variant="body2">
                    New total: {getCurrencySymbol(ticket.currency)}
                    {priceDeposit} + {getCurrencySymbol(ticket.currency)}
                    {(roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2)}
                    {' x '}
                    {ticket.num_monthly_payments} ={' '}
                    {getCurrencySymbol(ticket.currency)}
                    {total.toFixed(2)}.
                </Typography>
            </>
        );
    };

    render() {
        const {
            handleEdit,
            passenger,
            ticket,
            client,
            service,
            imagePreviewUrl,
            classes,
            disablePhoto,
            requireStudentId
        } = this.props;

        const { voucher } = this.state;

        const useZones =
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices &&
            passenger.zone;

        const priceDeposit = useZones
            ? ticket.zone_prices_deposit[passenger.zone]
            : ticket.price_deposit;

        const priceMonthly = useZones
            ? ticket.zone_prices_monthly[passenger.zone]
            : ticket.price_monthly;

        const monthlyPriceAfterDiscount = this.calcMonthlyPriceAfterDiscount(
            ticket,
            priceMonthly
        );

        const roundedMonthlyPricePenceAfterDiscount = Math.round(
            monthlyPriceAfterDiscount * 100
        );

        const totalMonthlyPriceAfterDiscount =
            (roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2) *
            ticket.num_monthly_payments;

        const total =
            Number(totalMonthlyPriceAfterDiscount) + Number(priceDeposit || 0);

        const voucherMessage = this.getVoucherMessage(
            ticket,
            total,
            roundedMonthlyPricePenceAfterDiscount,
            priceMonthly,
            priceDeposit
        );

        const hasFullValueVoucher =
            parseFloat(priceMonthly || 0) > 0 &&
            roundedMonthlyPricePenceAfterDiscount < 100;

        return (
            <div>
                <div className={classes.root}>
                    <ContentHeader
                        text="Ticket details"
                        id="ticket-details-heading"
                    />
                    {imagePreviewUrl && (
                        <div className={classes.imagePreview}>
                            <img src={imagePreviewUrl} alt="Ticket user" />
                        </div>
                    )}
                    <div className={classes.passengerInfo}>
                        <Typography variant="body2">
                            Passenger: {passenger.firstName}{' '}
                            {passenger.lastName}
                        </Typography>
                        {requireStudentId && passenger.id && (
                            <Typography variant="body2">
                                Student ID: {passenger.id}
                            </Typography>
                        )}
                        {passenger.stop && (
                            <Typography variant="body2">
                                Bus stop: {passenger.stop}
                            </Typography>
                        )}
                        {passenger.zone && (
                            <Typography variant="body2">
                                Zone: {passenger.zone}
                            </Typography>
                        )}
                        {passenger.zoneStop && (
                            <Typography variant="body2">
                                Bus stop: {passenger.zoneStop}
                            </Typography>
                        )}
                        {passenger.dest && (
                            <Typography variant="body2">
                                Destination: {passenger.dest}
                            </Typography>
                        )}
                        {passenger.yr && (
                            <Typography variant="body2">
                                Year group:{' '}
                                <span style={{ textTransform: 'capitalize' }}>
                                    {passenger.yr}
                                </span>
                            </Typography>
                        )}
                        <Typography variant="body2">
                            Ticket:{' '}
                            {formatTicketType(
                                service.name,
                                ticket.type,
                                ticket.sub_type,
                                ticket.travel_pattern,
                                ticket.name
                            )}
                        </Typography>
                        <Typography variant="body2">
                            Valid from: {formatDate(ticket.start)}
                        </Typography>
                        <Typography variant="body2">
                            Valid until: {formatDate(ticket.expiry)}
                        </Typography>
                        {parseFloat(priceMonthly) <= 0 && (
                            <>
                                <Typography variant="body2">
                                    Total price:{' '}
                                    {(formatPrice(total), ticket.currency)}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    onClick={handleEdit}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                        {parseFloat(priceMonthly) > 0 && (
                            <div>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    onClick={handleEdit}
                                >
                                    Edit
                                </Button>
                                <h3>Pricing</h3>
                                <Typography variant="body2">
                                    Initial payment due now:{' '}
                                    {formatPrice(priceDeposit, ticket.currency)}
                                </Typography>
                                <Typography variant="body2">
                                    Monthly payments:{' '}
                                    {formatPrice(
                                        monthlyPriceAfterDiscount,
                                        ticket.currency
                                    )}
                                    {' x '}
                                    {ticket.num_monthly_payments}
                                    {voucher && voucher.value && (
                                        <> (discounted)</>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    First monthly payment:{' 1st '}
                                    {moment(ticket.monthly_sub_start).format(
                                        'MMMM YYYY'
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    Total price:{' '}
                                    {formatPrice(total, ticket.currency)}
                                </Typography>
                                <p>
                                    <a
                                        className={classes.paymentHelp}
                                        href="https://help.shuttleid.uk/en/articles/7852307-how-payments-work-with-direct-debit"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        How Direct Debit payments work
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {parseFloat(priceMonthly) > 0 && (
                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            client={client}
                            setVoucher={this.setVoucher}
                            message={voucherMessage}
                        />
                    </div>
                )}

                {!hasFullValueVoucher && (
                    <div className={classes.root}>
                        <ContentHeader
                            text="Pay by Direct Debit"
                            id="pay-heading"
                        />
                        <DirectDebitPaymentForm
                            ticket={ticket}
                            service={service}
                            passenger={passenger}
                            client={client}
                            voucher={voucher}
                            disablePhoto={disablePhoto}
                        />
                    </div>
                )}
            </div>
        );
    }
}

ConfirmDirectDebitPurchase.propTypes = {
    handleEdit: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        photo: PropTypes.object,
        stop: PropTypes.string,
        dest: PropTypes.string
    }).isRequired,
    ticket: PropTypes.shape({
        start: PropTypes.string,
        expiry: PropTypes.string,
        price: PropTypes.string,
        price_monthly: PropTypes.string,
        price_deposit: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    imagePreviewUrl: PropTypes.string,
    classes: PropTypes.shape().isRequired,
    disablePhoto: PropTypes.bool,
    requireStudentId: PropTypes.bool
};

ConfirmDirectDebitPurchase.defaultProps = {
    imagePreviewUrl: '',
    disablePhoto: false,
    requireStudentId: false
};

export default withStyles(styles)(ConfirmDirectDebitPurchase);
